// ---------------------------------------------------------------------------
// TTS PLAY BUTTON — per-message ElevenLabs playback (v1.12.0).
//
// Renders under each agent bubble. Click → POST /api/tts/speak → streams MP3
// audio. Caches by SHA-of-text+voiceId so the same message replays without a
// fresh ElevenLabs call. Single-flight: clicking another bubble while one is
// playing pauses the previous.
//
// v2.19.39-sec11 — Style sobre aligné sur l'action bar : icône Volume2 lucide,
// pas de pilule blanche/ombre, hover discret slate-800/50.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";
import { Volume2 as IconVolume, Pause as IconPause, Loader2 as IconLoader, AlertCircle as IconAlert, RotateCcw as IconReplay } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Module-level so opening a new bubble can stop a previous play across
// component instances.
let CURRENT_AUDIO = null;

// Tiny in-memory cache: key → blob URL. Lives for the SPA session.
const CACHE = new Map();

function cacheKey(text, voiceId) { return `${voiceId || "default"}:${text.length}:${text.slice(0, 64)}`; }

export default function TTSPlayButton({ text, voiceId, mode, userId }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | done | error
  const audioRef = useRef(null);

  useEffect(() => () => {
    // Cleanup if unmounted mid-play.
    if (audioRef.current && CURRENT_AUDIO === audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      CURRENT_AUDIO = null;
    }
  }, []);

  const play = async () => {
    if (!text || !text.trim()) return;
    // If currently playing → toggle pause.
    if (audioRef.current && state === "playing") {
      try { audioRef.current.pause(); } catch (_) {}
      setState("idle");
      return;
    }
    // Stop any other playing audio first.
    if (CURRENT_AUDIO && CURRENT_AUDIO !== audioRef.current) {
      try { CURRENT_AUDIO.pause(); } catch (_) {}
    }
    setState("loading");
    const k = cacheKey(text, voiceId);
    let blobUrl = CACHE.get(k);
    if (!blobUrl) {
      try {
        const res = await fetch(`${API}/tts/speak`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, voice_id: voiceId, mode, userId, normalize: true }),
        });
        if (!res.ok) {
          const errBody = await res.text();
          throw new Error(`HTTP ${res.status} ${errBody.slice(0, 120)}`);
        }
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        CACHE.set(k, blobUrl);
      } catch (e) {
        setState("error");
        setTimeout(() => setState("idle"), 2000);
        return;
      }
    }
    const audio = new Audio(blobUrl);
    audioRef.current = audio;
    CURRENT_AUDIO = audio;
    audio.onended = () => { setState("done"); CURRENT_AUDIO = null; };
    audio.onpause = () => { if (state !== "done") setState("idle"); };
    audio.onplay  = () => setState("playing");
    audio.onerror = () => { setState("error"); setTimeout(() => setState("idle"), 2000); };
    try { await audio.play(); } catch (e) { setState("error"); setTimeout(() => setState("idle"), 2000); }
  };

  const ICON_PROPS = { width: 16, height: 16, strokeWidth: 1.75 };
  const icon =
    state === "loading" ? <IconLoader {...ICON_PROPS} className="civ-tts-spin" /> :
    state === "playing" ? <IconPause {...ICON_PROPS} /> :
    state === "error"   ? <IconAlert {...ICON_PROPS} /> :
    state === "done"    ? <IconReplay {...ICON_PROPS} /> :
                          <IconVolume {...ICON_PROPS} />;
  const label =
    state === "loading" ? "…" :
    state === "playing" ? "Pause" :
    state === "error"   ? "Erreur" :
    state === "done"    ? "Rejouer" :
                          "Lire à voix";

  return (
    <button
      type="button"
      onClick={play}
      data-testid="tts-play-button"
      disabled={state === "loading"}
      title="Lire ce message via ElevenLabs"
      className="civ-msg-action-btn civ-msg-action-tts"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 9px",
        fontSize: 12,
        background: "transparent",
        color: "#94a3b8",
        border: "1px solid transparent",
        borderRadius: 6,
        cursor: state === "loading" ? "wait" : "pointer",
        fontWeight: 500,
        lineHeight: 1,
      }}
    >{icon}<span>{label}</span></button>
  );
}
